import type { Locale } from '@seek/cmsu-cms-connect';
import {
  type Country,
  type Language,
  type Product,
  type SiteName,
  createAbsoluteUrl,
} from '@seek/melways-sites';

export type LocaleInfo = {
  locale: string;
  url: string;
};

export type LocalesInfo = {
  locales: LocaleInfo[];
};

/**
 * Extracts product name from site name
 */
const extractProduct = (siteName: SiteName): Product =>
  siteName.substring(0, siteName.indexOf('-')) as Product;

/**
 * Ensures route string starts with a forward slash
 */
const normalizeRoutePath = (path: string): string =>
  path.startsWith('/') ? path : `/${path}`;

/**
 * Creates a locale entry with URL
 */
const createLocaleEntry = (locale: string, url: string): LocaleInfo => ({
  locale,
  url,
});

/**
 * Creates a URL for the given country, product and language
 */
const buildUrl = (
  country: string,
  product: Product,
  path: string,
  language: string,
): string =>
  createAbsoluteUrl({
    country: country as Country,
    path,
    product,
    language: language as Language,
  });

/**
 * Creates English locale entry for a country
 */
const createEnglishLocaleEntry = (
  country: string,
  product: Product,
  path: string,
): LocaleInfo => ({
  locale: `en-${country}`,
  url: buildUrl(country, product, path, 'en'),
});

/**
 * Get locales with corresponding URLs based on site information and localizations
 */
const getLocales = (
  siteName: SiteName,
  route: string,
  sites: Array<{
    __typename?: 'Site' | undefined;
    name: string;
  }>,
  localizations: Array<{
    locale: Locale;
    title?: string;
  }>,
): LocaleInfo[] => {
  const product = extractProduct(siteName);
  const locales: LocaleInfo[] = [];
  const normalizedPath = normalizeRoutePath(route);

  // Filter and process relevant sites
  const relevantSites = sites
    .map((s) => s.name as SiteName)
    .filter((s) => s.includes(product));

  for (const site of relevantSites) {
    // Extract country code, treat 'root' explicitly as a Country
    const country = site.substring(site.lastIndexOf('-') + 1) as
      | Country
      | 'root';

    if (country === 'root') {
      // Handle root site (default locale)
      locales.push(
        createLocaleEntry('default', `https://www.seek.com${normalizedPath}`),
      );
      continue;
    }

    // Handle standard English locale sites
    if (!['id', 'th', 'hk'].includes(country)) {
      locales.push(createEnglishLocaleEntry(country, product, normalizedPath));
      continue;
    }

    // Handle special cases: id, th, hk
    handleSpecialCountry(
      country,
      product,
      normalizedPath,
      localizations,
      locales,
    );
  }

  return locales;
};

/**
 * Handle special country cases (id, th, hk)
 */
const handleSpecialCountry = (
  country: string,
  product: Product,
  path: string,
  localizations: Array<{ locale: Locale; title?: string }>,
  locales: LocaleInfo[],
): void => {
  // Try to find English locale for this country
  const enLocale = localizations.find((x) =>
    x.locale.includes(`en_${country.toUpperCase()}`),
  );

  // Add English locale if it exists
  if (enLocale) {
    locales.push(createEnglishLocaleEntry(country, product, path));
  }
  // Add default locale if it exists and title is not '-'
  else if (
    localizations.find((x) => x.locale.includes('default') && x.title !== '-')
  ) {
    locales.push(createEnglishLocaleEntry(country, product, path));
  }
  try {
    // Add native language locale if it exists (e.g., zh_HK, id_ID, th_TH)
    const nativeLocale = localizations.find(
      (x) =>
        x.locale.includes('zh_HK') ||
        x.locale.includes(`${country}_${country.toUpperCase()}`),
    );

    if (nativeLocale) {
      const url = buildUrl(
        country,
        product,
        path,
        nativeLocale.locale as string as Language | `${Language}-${string}`,
      );

      locales.push(
        createLocaleEntry(
          nativeLocale.locale.replace('_', '-').toLowerCase(),
          url,
        ),
      );
    }
  } catch {
    // console.error(`Error processing locale for country: ${country}`);
  }
};
export { getLocales };
